const specialties = [
  {
    id: 'new-construction',
    label: 'Specialty 01',
    title: 'New Construction Wiring',
    desc: 'From rough-in to final trim, we manage every phase of electrical installation for new builds. We collaborate closely with builders, general contractors, and inspectors to keep your project on schedule and fully code-compliant. Every circuit, panel, and outlet is planned and installed to the highest standard.',
    tag: 'Commercial & Residential',
    icon: 'ri-building-4-fill',
    image: 'https://public.readdy.ai/ai/img_res/1ede5a5322de2a5803168856a3e57bc5.jpg',
    wide: true,
  },
  {
    id: 'residential-repair',
    label: 'Specialty 02',
    title: 'Residential Service & Repair',
    desc: 'Fast, accurate diagnosis and reliable repairs for any residential electrical issue. From panel replacements and GFCI protection to whole-home rewiring — honest recommendations and clean, lasting work.',
    tag: 'Homeowners',
    icon: 'ri-home-gear-fill',
    image: 'https://readdy.ai/api/search-image?query=professional%20electrician%20upgrading%20modern%20electrical%20breaker%20panel%20box%20inside%20clean%20residential%20home%20organized%20wiring%20circuit%20breakers%20neat%20labeled%20panel%20professional%20tools%20premium%20home%20environment%20technical%20expertise&width=640&height=760&seq=specialty2&orientation=portrait',
    wide: false,
  },
  {
    id: 'ev-charger',
    label: 'Specialty 03',
    title: 'EV Charger Installation',
    desc: 'Future-proof your garage with a professionally installed Level 2 EV charger. We handle everything from wiring and panel capacity assessment to permitting and final inspection.',
    tag: 'Electric Vehicles',
    icon: 'ri-charging-pile-2-fill',
    image: 'https://readdy.ai/api/search-image?query=sleek%20modern%20electric%20vehicle%20charger%20wall%20mounted%20in%20clean%20upscale%20garage%20interior%20professional%20electrical%20installation%20premium%20home%20EV%20charging%20station%20minimalist%20white%20walls%20parked%20luxury%20car%20visible%20ambient%20lighting&width=640&height=760&seq=specialty3&orientation=portrait',
    wide: false,
  },
  {
    id: 'solar-panels',
    label: 'Specialty 04',
    title: 'Solar & Panel Upgrades',
    desc: 'Modernize your electrical system to handle today\'s energy demands. We upgrade panels to 200A+, prepare your home for solar integration, and ensure every system is safely and cleanly installed to code.',
    tag: 'Energy Efficiency',
    icon: 'ri-sun-fill',
    image: 'https://readdy.ai/api/search-image?query=modern%20residential%20solar%20panels%20installed%20on%20rooftop%20of%20premium%20upscale%20home%20blue%20sky%20sunny%20day%20clean%20professional%20installation%20suburban%20neighborhood%20high%20end%20home%20energy%20efficiency%20aerial%20perspective&width=900&height=640&seq=specialty4&orientation=landscape',
    wide: true,
  },
];

const FeaturedSpecialties = () => (
  <section id="specialties" className="relative bg-[#0A1628] py-28 overflow-hidden">
    {/* Animated background glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#00D9FF]/3 blur-[120px] pointer-events-none" />

    {/* Subtle grid */}
    <div className="absolute inset-0 opacity-[0.035] pointer-events-none">
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="dotgrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="#00D9FF"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dotgrid)"/>
      </svg>
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16 scroll-reveal">
        <div>
          <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">Our Specialties</span>
          <h2 className="text-4xl lg:text-5xl font-black text-white leading-tight">
            Expert Solutions for<br />
            <span className="text-[#8EC5E0]">Modern Electrical Needs</span>
          </h2>
        </div>
        <p className="text-[#4E6E8A] text-[15px] leading-relaxed max-w-sm">
          Premium-tier electrical services for Kalamazoo homes and businesses, delivered by certified professionals.
        </p>
      </div>

      {/* Top row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-5 scroll-reveal scroll-delay-1">
        {/* New Construction — large */}
        <SpecialtyCard specialty={specialties[0]} className="lg:col-span-3" horizontal />
        {/* Residential — tall */}
        <SpecialtyCard specialty={specialties[1]} className="lg:col-span-2" />
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 scroll-reveal scroll-delay-2">
        {/* EV Charger — tall */}
        <SpecialtyCard specialty={specialties[2]} className="lg:col-span-2" />
        {/* Solar — large */}
        <SpecialtyCard specialty={specialties[3]} className="lg:col-span-3" horizontal />
      </div>
    </div>
  </section>
);

interface Specialty {
  id: string;
  label: string;
  title: string;
  desc: string;
  tag: string;
  icon: string;
  image: string;
  wide: boolean;
}

interface SpecialtyCardProps {
  specialty: Specialty;
  className?: string;
  horizontal?: boolean;
}

const SpecialtyCard = ({ specialty, className = '', horizontal = false }: SpecialtyCardProps) => (
  <div
    className={`group relative rounded-2xl overflow-hidden border border-[#1E3048] hover:border-[#00D9FF]/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)] cursor-default ${className} ${horizontal ? 'min-h-[340px]' : 'min-h-[420px]'}`}
  >
    {/* Background image */}
    <img
      src={specialty.image}
      alt={specialty.title}
      className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
    />

    {/* Overlay */}
    <div className={`absolute inset-0 ${horizontal ? 'bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/60 to-[#0A1628]/20' : 'bg-gradient-to-t from-[#0A1628]/95 via-[#0A1628]/50 to-[#0A1628]/10'}`} />

    {/* Content */}
    <div className={`absolute ${horizontal ? 'left-0 top-0 bottom-0 flex flex-col justify-center p-10 max-w-sm' : 'bottom-0 left-0 right-0 p-8'}`}>
      {/* Label + tag */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[#00D9FF]/60 text-[10px] font-bold uppercase tracking-[3px]">{specialty.label}</span>
        <span className="px-2.5 py-1 bg-[#00D9FF]/10 border border-[#00D9FF]/20 rounded-full text-[#00D9FF] text-[10px] font-semibold tracking-wider whitespace-nowrap">{specialty.tag}</span>
      </div>

      {/* Icon + title */}
      <div className="flex items-center gap-3 mb-3">
        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className={`${specialty.icon} text-[#00D9FF] text-lg`} />
        </span>
        <h3 className="text-xl font-black text-white leading-tight">{specialty.title}</h3>
      </div>

      {/* Description */}
      <p className="text-[#7A9AB8] text-[13px] leading-relaxed mb-5">{specialty.desc}</p>

      {/* CTA link */}
      <a
        href="#contact"
        className="inline-flex items-center gap-2 text-[#00D9FF] text-[13px] font-semibold group/link cursor-pointer"
      >
        <span className="relative">
          Get a Quote
          <span className="absolute -bottom-0.5 left-0 right-0 h-[1px] bg-[#00D9FF] origin-left scale-x-0 group-hover/link:scale-x-100 transition-transform duration-300" />
        </span>
        <span className="w-4 h-4 flex items-center justify-center group-hover/link:translate-x-1 transition-transform duration-300">
          <i className="ri-arrow-right-line" />
        </span>
      </a>
    </div>
  </div>
);

export default FeaturedSpecialties;
